import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Settings, BookOpen, MessageSquare, Code, Activity } from 'lucide-react';
import AgentSettings from '../components/AgentSettings';
import KnowledgePanel from '../components/KnowledgePanel';
import ChatTester from '../components/ChatTester';
import EmbedCode from '../components/EmbedCode';
import LogsPanel from '../components/LogsPanel';
import { agentsAPI } from '../lib/api';

const AgentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('settings');

  const loadAgent = useCallback(async () => {
    try {
      const data = await agentsAPI.get(id);
      setAgent(data);
    } catch (error) {
      
      console.error('Error loading agent:', error);
      setAgent(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadAgent();
  }, [loadAgent]);

  const handleAgentUpdate = (updatedAgent) => {
    setAgent(updatedAgent);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-800 rounded w-1/4"></div>
          <div className="h-64 bg-slate-800 rounded"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!agent) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-slate-400">Agent not found</p>
          <Button variant="link" onClick={() => navigate('/dashboard/agents')} className="text-indigo-400">
            Back to Agents
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/dashboard/agents')}
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">{agent.name}</h1>
            <p className="text-slate-400 text-sm">{agent.description || 'No description'}</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-900/50 border border-slate-800 p-1">
            <TabsTrigger 
              value="settings" 
              className="data-[state=active]:bg-slate-800 data-[state=active]:text-white text-slate-400"
            >
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </TabsTrigger>
            <TabsTrigger 
              value="knowledge" 
              className="data-[state=active]:bg-slate-800 data-[state=active]:text-white text-slate-400"
            >
              <BookOpen className="h-4 w-4 mr-2" />
              Knowledge
            </TabsTrigger>
            <TabsTrigger 
              value="test" 
              className="data-[state=active]:bg-slate-800 data-[state=active]:text-white text-slate-400"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Test Chat
            </TabsTrigger>
            <TabsTrigger 
              value="embed" 
              className="data-[state=active]:bg-slate-800 data-[state=active]:text-white text-slate-400"
            >
              <Code className="h-4 w-4 mr-2" />
              Embed
            </TabsTrigger>
            <TabsTrigger 
              value="logs" 
              className="data-[state=active]:bg-slate-800 data-[state=active]:text-white text-slate-400"
            >
              <Activity className="h-4 w-4 mr-2" />
              Logs
            </TabsTrigger>
          </TabsList>

          <TabsContent value="settings" className="mt-6">
            <AgentSettings agent={agent} onUpdate={handleAgentUpdate} />
          </TabsContent>

          <TabsContent value="knowledge" className="mt-6">
            <KnowledgePanel agentId={agent.agent_id} />
          </TabsContent>

          <TabsContent value="test" className="mt-6">
            <ChatTester agent={agent} />
          </TabsContent>

          <TabsContent value="embed" className="mt-6">
            <EmbedCode agent={agent} />
          </TabsContent>

          <TabsContent value="logs" className="mt-6">
            <LogsPanel agentId={agent.agent_id} />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default AgentDetail;
